import animate_carousel_scroll from './animate_carousel_scroll.js';

/** Start after one second, then rest two seconds between 1.2-second slide transitions. */
export default function carousel_auto_advance(node, enabled = false) {
  const document = node.ownerDocument;
  const window = document.defaultView;
  const interaction_root = node.closest('.story-layout') ?? node;
  const reduced_motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hover_capable = window.matchMedia('(any-hover: hover)');
  let stopped = false;
  let has_advanced = false;
  let destroyed = false;
  let visible = false;
  // A visible page can mount before it receives keyboard focus (for example in
  // an embedded preview). Do not require an initial click to start playback.
  // Subsequent window blur/focus events still suspend and resume it.
  let window_active = true;
  let hovered = false;
  let pointer;
  let timer = null;
  let moving = false;
  let cancel_animation;
  let restore_wrap;
  let resting_card;
  let resting_geometry;
  let visibility_observer;
  let resize_observer;
  let mutation_observer;
  let remove_listeners = [];

  function cards() {
    return Array.from(node.children).filter(child => child.matches('.preview-card'));
  }

  function controls_hovered() {
    if (!hover_capable.matches) return false;
    // Native pagination is made of pseudo-elements, not DOM buttons. CSS reports
    // its actual hover state without treating the surrounding image as a control.
    const hovered_control = (element, pseudo) => window.getComputedStyle(element, pseudo)
      .getPropertyValue('--carousel-controls-hovered').trim() === '1';
    return hovered_control(node, '::scroll-marker-group') ||
      cards().some(card => hovered_control(card, '::scroll-marker'));
  }

  function refresh_hover() {
    const next = controls_hovered();
    if (next === hovered) return;
    hovered = next;
    schedule();
  }

  function measure() {
    const items = cards();
    const rect = node.getBoundingClientRect();
    // Cover zoom and scaled ancestors put rectangles in different units from scrollLeft.
    const scale = node.offsetWidth ? rect.width / node.offsetWidth : 0;
    if (!scale || !node.clientWidth || node.scrollWidth <= node.clientWidth + 1 || items.length < 2) return;
    const center = rect.left + (node.clientLeft + node.clientWidth / 2) * scale;
    const positions = items.map(card => {
      const card_rect = card.getBoundingClientRect();
      return { card, width: card_rect.width, left: node.scrollLeft + (card_rect.left + card_rect.width / 2 - center) / scale };
    });
    if (positions.some(position => !position.width)) return;
    const nearest = positions.reduce((best, position) =>
      Math.abs(position.left - node.scrollLeft) < Math.abs(best.left - node.scrollLeft) ? position : best);
    return { positions, nearest };
  }

  function remember_position(card = measure()?.nearest.card) {
    resting_card = card;
    resting_geometry = measure();
  }

  function geometry_changed(geometry, previous) {
    return geometry?.positions.some((position, index) => {
      const before = previous?.positions[index];
      return !before || position.card !== before.card || Math.abs(position.left - before.left) > 2;
    });
  }

  function restore_property(style, property, value, priority) {
    if (value) style.setProperty(property, value, priority);
    else style.removeProperty(property);
  }

  function settle(card) {
    const position = measure()?.positions.find(position => position.card === card);
    if (position) node.scrollTo({ left: position.left, behavior: 'instant' });
  }

  function prepare_wrap(last) {
    const order = last.style.getPropertyValue('order');
    const order_priority = last.style.getPropertyPriority('order');
    const snap = node.style.getPropertyValue('scroll-snap-type');
    const snap_priority = node.style.getPropertyPriority('scroll-snap-type');
    node.style.setProperty('scroll-snap-type', 'none', 'important');
    // Move only the visual flex order. The same last slide stays in view while the first becomes adjacent.
    last.style.setProperty('order', '-1', 'important');
    settle(last);
    const restore = card => {
      restore_property(last.style, 'order', order, order_priority);
      settle(card);
      restore_property(node.style, 'scroll-snap-type', snap, snap_priority);
      settle(card);
    };
    // During a native gesture keep the visual order until snapping has finished.
    // Reordering while two slides are visible would make one of them jump away.
    restore.release = () => restore_property(node.style, 'scroll-snap-type', snap, snap_priority);
    return restore;
  }

  function cancel_timer() {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  }

  function cancel_motion(handoff = false) {
    if (!moving && !restore_wrap) return;
    const nearest = handoff ? undefined : measure()?.nearest.card;
    cancel_animation?.();
    cancel_animation = undefined;
    if (handoff) {
      moving = false;
      restore_wrap?.release();
      return;
    }
    const restore = restore_wrap;
    restore_wrap = undefined;
    if (restore) restore(nearest);
    else if (nearest) settle(nearest);
    remember_position(nearest);
    moving = false;
  }

  function environment_ready() {
    return enabled && !destroyed && visible &&
      !document.hidden && window_active && !reduced_motion.matches;
  }

  function can_play() {
    return environment_ready() && !stopped && !pointer;
  }

  function schedule() {
    hovered = controls_hovered();
    if (!environment_ready()) {
      cancel_timer();
      cancel_motion();
    } else if (hovered || stopped || pointer) {
      // Reading, control hover, or a vertical touch pan must not snap a slide
      // mid-transition. Only actual horizontal navigation takes over the motion.
      cancel_timer();
    } else if (timer === null && !moving) {
      timer = window.setTimeout(advance, has_advanced ? 2000 : 1000);
    }
  }

  function advance() {
    timer = null;
    hovered = controls_hovered();
    if (!can_play() || hovered) return;
    const geometry = measure();
    if (!geometry) return; // Resize/card observers retry after initially empty layout settles.
    const index = geometry.positions.indexOf(geometry.nearest);
    const target = geometry.positions[(index + 1) % geometry.positions.length].card;
    moving = true;
    if (index === geometry.positions.length - 1) {
      restore_wrap = prepare_wrap(geometry.nearest.card);
    }
    const animation_geometry = measure();
    const destination = animation_geometry?.positions.find(position => position.card === target);
    if (!destination) {
      cancel_motion();
      return;
    }
    cancel_animation = animate_carousel_scroll(node, destination.left, () => {
      cancel_animation = undefined;
      restore_wrap?.(target);
      restore_wrap = undefined;
      remember_position(target);
      moving = false;
      has_advanced = true;
      schedule();
    }, () => {
      if (geometry_changed(measure(), animation_geometry)) layout_changed();
      else relinquish(true);
    });
  }

  function relinquish(handoff = false) {
    stopped = true;
    cancel_timer();
    if (handoff) cancel_motion(true);
  }

  function pointer_down(event) {
    if (controls_hovered()) return relinquish(true);
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    if (pointer) return;
    pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
    schedule();
  }

  function pointer_move(event) {
    if (event.pointerType === 'mouse') refresh_hover();
    if (!pointer || event.pointerId !== pointer.id) return;
    const x = Math.abs(event.clientX - pointer.x);
    const y = Math.abs(event.clientY - pointer.y);
    if (x > 6 && x > y) relinquish(true);
  }

  function pointer_end(event) {
    if (!pointer || event.pointerId !== pointer.id) return;
    pointer = undefined;
    schedule();
  }

  function listen(target, event, handler, options = { passive: true, capture: true }) {
    target.addEventListener(event, handler, options);
    remove_listeners.push(() => target.removeEventListener(event, handler, options));
  }

  function observe_sizes() {
    if (!resize_observer) return;
    resize_observer.disconnect();
    resize_observer.observe(node);
    cards().forEach(card => resize_observer.observe(card));
  }

  function layout_changed() {
    cancel_timer();
    cancel_motion();
    const geometry = measure();
    // Keep the logical resting slide: ResizeObserver may run before or after
    // the browser re-snaps it to its new pixel offset.
    if (!geometry?.positions.some(position => position.card === resting_card)) resting_card = geometry?.nearest.card;
    resting_geometry = geometry;
    schedule();
  }

  function start() {
    if (!enabled || destroyed || visibility_observer || !window.IntersectionObserver) return;
    window_active = true;
    hovered = controls_hovered();
    remember_position();
    listen(interaction_root, 'focusin', event => {
      if (event.target.matches?.(':focus-visible')) relinquish();
    });
    listen(interaction_root, 'click', event => {
      if (controls_hovered()) relinquish(true);
      else if (event.target.closest?.('a[href], button, input, select, textarea')) relinquish();
    });
    listen(interaction_root, 'pointerdown', pointer_down);
    listen(window, 'pointermove', pointer_move);
    for (const event of ['pointerup', 'pointercancel']) listen(window, event, pointer_end);
    listen(interaction_root, 'wheel', event => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || (event.shiftKey && event.deltaY)) relinquish(true);
    });
    listen(interaction_root, 'keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') relinquish(true);
    });
    listen(node, 'scroll', () => {
      if (moving || stopped) return;
      const geometry = measure();
      if (!geometry) return;
      const expected = geometry.positions.find(position => position.card === resting_card);
      // A bare scroll event can be automatic re-snapping after layout, including
      // an event delivered before ResizeObserver. It is not itself user intent.
      if (geometry_changed(geometry, resting_geometry) || (expected && Math.abs(node.scrollLeft - expected.left) <= 2)) {
        resting_geometry = geometry;
      } else relinquish(true);
    });
    listen(node, 'scrollend', () => {
      if (moving || !restore_wrap) return;
      const nearest = measure()?.nearest.card;
      const restore = restore_wrap;
      restore_wrap = undefined;
      restore(nearest);
      remember_position(nearest);
    });
    listen(interaction_root, 'pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      refresh_hover();
    }, { passive: true });
    listen(interaction_root, 'pointerleave', () => {
      hovered = false;
      schedule();
    }, { passive: true });
    listen(document, 'visibilitychange', schedule);
    listen(window, 'focus', () => { window_active = true; schedule(); }, { passive: true });
    listen(window, 'blur', () => { window_active = false; schedule(); }, { passive: true });
    listen(reduced_motion, 'change', schedule);
    const observer = new window.IntersectionObserver(entries => {
      if (visibility_observer !== observer) return;
      for (const entry of entries) {
        if (entry.target !== node) continue;
        // The image can be scaled down inside a much taller carousel box.
        // Start as soon as the carousel intersects, including its mount callback;
        // waiting for a fraction of the unscaled box delays visible previews.
        visible = entry.isIntersecting;
        schedule();
      }
    }, { threshold: 0 });
    visibility_observer = observer;
    observer.observe(node);
    if (window.ResizeObserver) {
      resize_observer = new window.ResizeObserver(layout_changed);
      observe_sizes();
    }
    if (window.MutationObserver) {
      mutation_observer = new window.MutationObserver(() => {
        layout_changed();
        observe_sizes();
      });
      mutation_observer.observe(node, { childList: true });
    }
    if (interaction_root.contains(document.activeElement) && document.activeElement?.matches?.(':focus-visible')) relinquish();
  }

  function stop() {
    cancel_timer();
    cancel_motion();
    visibility_observer?.disconnect();
    resize_observer?.disconnect();
    mutation_observer?.disconnect();
    visibility_observer = resize_observer = mutation_observer = undefined;
    remove_listeners.forEach(remove => remove());
    remove_listeners = [];
    visible = false;
    pointer = undefined;
  }

  function update(next) {
    enabled = Boolean(next);
    if (enabled) {
      start();
      schedule();
    } else stop();
  }

  update(enabled);
  return {
    update,
    destroy() {
      destroyed = true;
      stop();
    },
  };
}
